import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Edit, Trash2, PlusCircle, ChevronUp } from "react-feather";
import { Table } from "antd";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import BranchModal from "../../core/modals/peoples/BranchModal";
import { useDispatch, useSelector } from "react-redux";
import { setToogleHeader, setBranchData, setCurrentBranch } from "../../core/redux/action";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useToast } from "../../Toaster/ToastContext";
import { Switch } from "antd";
import { apiClient } from "../../services/apiClient";

const Branches = () => {

  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const branchData = useSelector((state) => state.branchdata);
  const rawCurrentBranch = useSelector(
    (state) => state.currentBranch,
    (prev, next) => prev?.id === next?.id
  );
  const currentBranch = useMemo(() => rawCurrentBranch, [rawCurrentBranch?.id]);

  const [branches, setBranches] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { showToast } = useToast();
  const hasFetched = useRef(false);
  const MySwal = withReactContent(Swal);

  const fetchBranches = useCallback(async () => {
    if (hasFetched.current && !refresh && !loading) {
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.post('/paginate-branch', {
        pageIndex: 0,
        pageSize: 50,
        previousPageIndex: 0,
      })
      const result = await response;
      // console.log("fetchBranches: Response received", result);
      if (result.success) {
        const transformedData = result.data.map((branch) => ({
          ...branch,
          name: branch.name || "",
          email: branch.email || "",
          phone: branch.phone || "",
          address: branch.address || "",
          status: branch.status === 1,
        }));
        setBranches(transformedData);
        setFilteredBranches(transformedData);
        dispatch(setBranchData(transformedData));
      } else {
        showToast(result.message || "Failed to fetch branches", "error", "Branches");
      }
    } catch (error) {
      console.error("fetchBranches: Error", error);
      showToast(error.message || "Failed to fetch branches", "error", "Branches");
    } finally {
      setLoading(false);
      setRefresh(false);
      hasFetched.current = true;
    }
  }, [showToast, dispatch, refresh]);

  useEffect(() => {
    if (!hasFetched.current || refresh) {
      fetchBranches();
    }
  }, [fetchBranches, refresh]);

  useEffect(() => {
    setBranches(branchData);
    setFilteredBranches(branchData);
    // console.log("useEffect: Synced branches with branchData", branchData);
  }, [branchData]);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    const filtered = branches.filter(
      (item) =>
        item.name.toLowerCase().includes(term) ||
        item.email.toLowerCase().includes(term) ||
        item.phone.toLowerCase().includes(term)
    );
    setFilteredBranches(filtered);
  };

  const handleAddBranch = () => dispatch(setCurrentBranch(null));

  const handleEditBranch = useCallback((branch) => {
    // console.log("handleEditBranch: Setting currentBranch", branch);
    dispatch(setCurrentBranch(branch));
  }, [dispatch]);

  const renderCollapseTooltip = (props) => (
    <Tooltip id="collapse-tooltip" {...props}>Collapse</Tooltip>
  );

  const showConfirmationAlert = useCallback(async (record) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Yes, delete it!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          // const response = await deleteBranch(record.id);
          // const data = await response.json();
          const data = await apiClient.post('/delete-branch', {
            id: record.id,
          });
          if (data.success) {
            showToast(data.msg || "Branch deleted successfully", "success", "Branches");
            setRefresh(true);
          } else {
            showToast(data.msg || "Failed to delete branch", "error", "Branches");
          }
        } catch (error) {
          console.error("showConfirmationAlert: Error", error);
          showToast(error.message || "Failed to delete branch", "error", "Branches");
        } finally {
          setLoading(false);
        }
      }
    });
  }, [showToast, MySwal]);

  const columns = useMemo(() => [
    // { render: () => <label className="checkboxs"><input type="checkbox" /><span className="checkmarks" /></label> },
    { title: "Name", dataIndex: "name", sorter: (a, b) => a.name.length - b.name.length },
    { title: "Email", dataIndex: "email", sorter: (a, b) => a.email.length - b.email.length },
    { title: "Phone", dataIndex: "phone", sorter: (a, b) => a.phone.length - b.phone.length },
    { title: "Address", dataIndex: "address", sorter: (a, b) => a.address.length - b.address.length },
    { title: "Status", dataIndex: "status", render: (status) => <Switch checked={status} />, sorter: (a, b) => (a.status ? 1 : 0) - (b.status ? 1 : 0) },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, record) => (
        <td className="action-table-data">
          <div className="edit-delete-action">
            {/* <Link className="me-2 p-2" to="#"><Eye className="feather-view" /></Link> */}
            <Link className="me-2 p-2" to="#" data-bs-toggle="modal" data-bs-target="#branch-modal" onClick={() => handleEditBranch(record)}>
              <Edit className="feather-edit" />
            </Link>
            <Link className="confirm-text p-2" to="#" onClick={() => showConfirmationAlert(record)}>
              <Trash2 className="feather-trash-2" />
            </Link>
          </div>
        </td>
      ),
    },
  ], [handleEditBranch, showConfirmationAlert]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title"><h4>Branch List</h4><h6>Manage Your Branches</h6></div>
          </div>
          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link data-bs-toggle="tooltip" data-bs-placement="top" id="collapse-header" className={data ? "active" : ""} onClick={() => dispatch(setToogleHeader(!data))}>
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
          <div className="page-btn">
            <Link to="#" className="btn btn-added" data-bs-toggle="modal" data-bs-target="#branch-modal" onClick={handleAddBranch}>
              <PlusCircle className="me-2" /> Add New Branch
            </Link>
          </div>
        </div>
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set"><div className="search-input"><input type="text" placeholder="Search by Name, Email, or Phone" className="form-control form-control-sm formsearch" onChange={handleSearch} /><Link to="#" className="btn btn-searchset"><i data-feather="search" className="feather-search" /></Link></div></div>
            </div>
            <div className="table-responsive">
              <Table className="table datanew" columns={columns} dataSource={filteredBranches} rowKey={(record) => record.id} loading={loading} rowSelection={null}/>
            </div>
          </div>
        </div>
      </div>
      <BranchModal branch={currentBranch} onSave={() => setRefresh(true)} />
    </div>
  );
};

export default Branches;